import { cva } from "class-variance-authority";
import type { HTMLAttributes } from "react";

import { cn } from "./cn.js";
import type { PublishedFeedState } from "../api/types.js";

/**
 * How a feed's state is said, and how quietly.
 *
 * A feed that is fine gets no colour and no box: it is a dot and two words. So does a
 * feed the operator has switched off — that is his own decision, not a fault. Only a
 * feed that is not fine is allowed to spend any of the surface's attention, and even
 * then it is a tint and a hairline, never an alarm.
 */
export type StateTone = "ok" | "warn" | "err" | "idle";

const feedStateVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full font-mono text-[0.6875rem] leading-none tracking-[0.04em] whitespace-nowrap",
  {
    variants: {
      tone: {
        ok: "text-ink-3",
        warn: "border border-[color-mix(in_oklch,var(--warn)_35%,var(--border))] bg-[color-mix(in_oklch,var(--warn)_10%,transparent)] px-2 py-1 text-warn",
        err: "border border-[color-mix(in_oklch,var(--err)_35%,var(--border))] bg-[color-mix(in_oklch,var(--err)_10%,transparent)] px-2 py-1 text-err",
        idle: "text-ink-3",
      },
    },
    defaultVariants: { tone: "idle" },
  },
);

const dotClasses: Record<StateTone, string> = {
  ok: "bg-ok",
  warn: "bg-warn",
  err: "bg-err",
  idle: "bg-ink-3",
};

interface StateCopy {
  tone: StateTone;
  label: string;
  detail: string;
}

const STATE_COPY: Record<"healthy" | "degraded" | "down" | "none", StateCopy> =
  {
    healthy: {
      tone: "ok",
      label: "On air",
      detail: "Playing as scheduled.",
    },
    degraded: {
      tone: "warn",
      label: "Fallback",
      detail: "Playing fallback audio.",
    },
    down: {
      tone: "err",
      label: "Off air",
      detail: "Listeners hear nothing.",
    },
    none: {
      tone: "idle",
      label: "Not established",
      detail: "No status yet.",
    },
  };

export function feedStateCopy(state: PublishedFeedState | null): StateCopy {
  return STATE_COPY[state ?? "none"];
}

/** The pill itself: a dot, a word, and colour only when colour means something. */
export function TonePill({
  className,
  label,
  pulse = false,
  tone,
  ...props
}: HTMLAttributes<HTMLSpanElement> & {
  label: string;
  pulse?: boolean;
  tone: StateTone;
}) {
  return (
    <span className={cn(feedStateVariants({ tone }), className)} {...props}>
      <span
        aria-hidden="true"
        className={cn(
          "size-1.5 shrink-0 rounded-full",
          dotClasses[tone],
          pulse &&
            tone === "ok" &&
            "animate-[radio-pulse_2.4s_ease-in-out_infinite]",
        )}
      />
      {label}
    </span>
  );
}

export function FeedStatePill({
  className,
  state,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { state: PublishedFeedState | null }) {
  const { tone, label, detail } = feedStateCopy(state);
  return (
    <TonePill
      className={className}
      label={label}
      pulse
      title={detail}
      tone={tone}
      {...props}
    />
  );
}
